// client/src/components/pages/LexiStar/Replay/replayZones.ts
//
// ST-00272: the pure side of the zones on the Replay map. The server says which version of
// each zone was in force when (GET /api/geofences/in-force, utils/geofenceInForce); the map
// shows, at the replay clock, the versions in force then — a zone created after that time,
// or retired before it, is not drawn, and a zone whose shape changed shows the shape it had.
// Styling is Live View's (Geofencing/zoneTypes colours: a light fill and a solid outline).
//
// The choices — zones on or off, and which zone types — are remembered per user
// (utils/userStorage), like Live View's layer chips (which are per browser).
import type { Feature, FeatureCollection, Polygon } from 'geojson';
import api from '../../../../services/api';
import { circleToPolygonRing as circleToPolygonRingJs } from '../../../map/geo';
import {
  ZONE_TYPES as ZONE_TYPES_JS,
  getZoneTypeMeta as getZoneTypeMetaJs,
} from '../../Geofencing/zoneTypes';
import { safeStorage, userStorageKey } from '../../../../utils/userStorage';
import type { ZoneInForce, ZonesInForceResponse } from '../../../../types/clientPages/geofencing';

export type { ZoneInForce };

interface ZoneTypeMeta {
  id: string;
  label: string;
  singular: string;
  color: string;
}
// Typed views of the JavaScript modules.
const circleToPolygonRing = circleToPolygonRingJs as unknown as (
  lng: number,
  lat: number,
  radiusMeters: number,
  steps?: number
) => [number, number][] | null;
export const ZONE_TYPES = ZONE_TYPES_JS as unknown as ZoneTypeMeta[];
export const zoneTypeMeta = getZoneTypeMetaJs as unknown as (id: string) => ZoneTypeMeta;

/** Zone names are drawn on the map from this zoom (closer in: names on hover or tap). */
export const ZONE_LABEL_ZOOM = 14;
export const ZONE_PREFS_KEY = 'lexistar.replay.zones';

export interface ZonePrefs {
  /** The map's Zones toggle (default on). */
  on: boolean;
  /** Per zone type; a missing type is shown. */
  types: Record<string, boolean>;
}

export const DEFAULT_ZONE_PREFS: ZonePrefs = { on: true, types: {} };

export function loadZonePrefs(): ZonePrefs {
  const key = userStorageKey(ZONE_PREFS_KEY);
  if (!key) return { ...DEFAULT_ZONE_PREFS, types: {} };
  try {
    const stored: unknown = JSON.parse(safeStorage.get(key) || 'null');
    if (stored && typeof stored === 'object') {
      const s = stored as { on?: unknown; types?: unknown };
      const types: Record<string, boolean> = {};
      if (s.types && typeof s.types === 'object') {
        Object.entries(s.types as Record<string, unknown>).forEach(([k, v]) => {
          if (typeof v === 'boolean') types[k] = v;
        });
      }
      return { on: typeof s.on === 'boolean' ? s.on : true, types };
    }
  } catch {
    /* corrupt storage: the defaults */
  }
  return { ...DEFAULT_ZONE_PREFS, types: {} };
}

export function saveZonePrefs(prefs: ZonePrefs): void {
  const key = userStorageKey(ZONE_PREFS_KEY);
  if (key) safeStorage.set(key, JSON.stringify(prefs));
}

/** The zones as they were in [from, to) (rejects on 403 / errors; the caller hides zones). */
export async function fetchZonesInForce(from: number, to: number): Promise<ZoneInForce[]> {
  const res = (await api.get('/geofences/in-force', {
    params: { from: new Date(from).toISOString(), to: new Date(to).toISOString() },
  })) as { data?: ZonesInForceResponse } | undefined;
  const list = res?.data?.data;
  return Array.isArray(list) ? list : [];
}

/** The window to ask for: the replay's time bounds, widened to whole minutes. */
export function zoneWindow(bounds: { start: number; end: number } | null): [number, number] | null {
  if (!bounds || !Number.isFinite(bounds.start) || !Number.isFinite(bounds.end)) return null;
  const MIN = 60000;
  const from = Math.floor(bounds.start / MIN) * MIN;
  const to = Math.max(Math.ceil(bounds.end / MIN) * MIN, from + MIN);
  return [from, to];
}

export interface ZoneShape {
  /** geofence_id:version:from — one piece of time a version was in force. */
  key: string;
  zone: ZoneInForce;
  from: number;
  /** Infinity when still in force at the end of the window. */
  to: number;
  ring: [number, number][];
  /** The ring's centre. */
  center: [number, number];
  /** Where the name goes: above the zone's top edge, clear of the trucks inside it. */
  labelAt: [number, number];
  color: string;
  typeLabel: string;
}

/** A zone version's outline as a closed [lon, lat] ring (circles as 64-gons, like Live View). */
export function zoneRing(z: ZoneInForce): [number, number][] | null {
  const g = z.geometry;
  if (!g) return null;
  if (g.type === 'Polygon') {
    const ring = (g.coordinates?.[0] || []).filter(
      (p): p is [number, number] => Array.isArray(p) && Number.isFinite(p[0]) && Number.isFinite(p[1])
    );
    if (ring.length < 3) return null;
    const [f, l] = [ring[0]!, ring[ring.length - 1]!];
    return f[0] === l[0] && f[1] === l[1] ? ring : [...ring, f];
  }
  if (g.type === 'Point') {
    const [lon, lat] = g.coordinates || [];
    if (!Number.isFinite(lon) || !Number.isFinite(lat) || !z.radius_m) return null;
    return circleToPolygonRing(lon as number, lat as number, Number(z.radius_m));
  }
  return null;
}

export function zoneShapes(zones: ZoneInForce[]): ZoneShape[] {
  const out: ZoneShape[] = [];
  for (const z of zones) {
    const ring = zoneRing(z);
    if (!ring || ring.length < 4) continue;
    const pts = ring.slice(0, -1);
    const center: [number, number] = [
      pts.reduce((s, p) => s + p[0], 0) / pts.length,
      pts.reduce((s, p) => s + p[1], 0) / pts.length,
    ];
    const meta = zoneTypeMeta(z.zone_type);
    const from = Date.parse(z.from);
    const to = z.to ? Date.parse(z.to) : Infinity;
    out.push({
      key: `${z.geofence_id}:${z.version}:${z.from}`,
      zone: z,
      from: Number.isFinite(from) ? from : -Infinity,
      to: Number.isFinite(to) ? to : Infinity,
      ring,
      center,
      labelAt: [center[0], Math.max(...pts.map((p) => p[1]))],
      color: meta.color,
      typeLabel: meta.singular,
    });
  }
  return out;
}

/**
 * The shapes drawn at `at` (the replay clock): in force then, of a type that is switched on.
 * Before the clock is known, the first instant of the window stands in.
 */
export function shapesAt(
  shapes: ZoneShape[],
  at: number | null,
  types: Record<string, boolean>
): ZoneShape[] {
  const t = at ?? Math.min(...shapes.map((s) => (Number.isFinite(s.from) ? s.from : Infinity)));
  return shapes.filter(
    (s) => s.from <= t && t < s.to && types[s.zone.zone_type] !== false
  );
}

/** The zone layer's GeoJSON (properties drive the paint: colour, and the name for hover). */
export function shapesCollection(shapes: ZoneShape[]): FeatureCollection<Polygon> {
  return {
    type: 'FeatureCollection',
    features: shapes.map(
      (s): Feature<Polygon> => ({
        type: 'Feature',
        geometry: { type: 'Polygon', coordinates: [s.ring] },
        properties: { key: s.key, color: s.color, name: s.zone.name, type: s.typeLabel },
      })
    ),
  };
}

/** The zone types in the replay's zones, in Live View's (taxonomy) order. */
export function presentZoneTypes(zones: ZoneInForce[]): ZoneTypeMeta[] {
  const present = new Set(zones.map((z) => z.zone_type));
  return ZONE_TYPES.filter((t) => present.has(t.id));
}
